import React from 'react';
import noiseSuppressionChangelog from './noise-suppression.changes.md';

import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { HandleType, NodeToolbar, Position } from 'reactflow';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import {
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  NoiseSuppressionState,
} from '../..';

export const config: NodeConfig = {
  version: '1.1',
  title: 'Noise Suppression',
  identifier: 'NoiseSuppression',
  command_id: 'call.noise_suppression',
  icon: 'telnyx',
  background: '#000000',
  changelog: noiseSuppressionChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'call.noise_suppression.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'call.noise_suppression.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      id: 'call.noise_suppression.suppressed',
      label: 'Executed',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'call.noise_suppression.output_data',
      type: 'source' as HandleType,
      variant: 'data',
    },
  ],
  webhooks: [],
};

export const state: NoiseSuppressionState = {
  call_control_id: { type: NodeInputType.Expression, value: '' },
  direction: { type: NodeInputType.Option, value: 'both' },
  client_state: { type: NodeInputType.Expression, value: '' },
};

export const fields: NodeField[] = [
  {
    id: 'call_control_id',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Call Control ID',
    description: 'Unique identifier and token for controlling the call',
    placeholder: 'A unique identifier for controlling the call',
    required: false,
  },
  {
    id: 'direction',
    tab: '1',
    type: NodeFieldType.OptionExpression,
    default_type: NodeInputType.Option,
    label: 'Direction',
    description: 'The direction of the audio stream to be noise suppressed.',
    required: false,
    placeholder: 'Audio Stream Suppression Direction',
    options: [
      { label: 'Both', value: 'both' },
      { label: 'Inbound', value: 'inbound' },
      { label: 'Outbound', value: 'outbound' },
    ],
  },
  {
    id: 'client_state',
    tab: '1',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Expression,
    label: 'Client State',
    description: 'Use this field to add state to every subsequent webhook. It must be a valid Base-64 encoded string.',
    placeholder: 'Add state to each subsequent webhook.',
    required: false,
  },
];

export const output: NodeOutput = {
  default: {
    call: {
      noise_suppression: {
        call_control_id: 'string',
      },
    },
  },
  dynamic: null,
};

export const NoiseSuppression: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'call.noise_suppression') return;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
